// src/api.ts
import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse
} from 'axios';
import GlobalVariable from './GlobalVariable';
import { getNativeAccessToken, requestNativeRefresh, resolveNativeRefreshMode } from './nativeRefreshBroker';
import {isMissingStaffProfile} from './staffAccess';
import {
    exportSessionToNative,
    getBrowserSessionProtocolPayload,
    getFingerprint,
    getNativeSessionModeHeaders,
    getStoredRefreshToken,
    isInWebView,
    notifyNativeSessionInvalid,
    persistRefreshToken,
} from './authSessionBridge';

type RetryAxiosRequestConfig = AxiosRequestConfig & { _retry?: boolean };
const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || '';
const REQUEST_TIMEOUT_MS = 20000;
let refreshPromise: Promise<{data: RefreshResponse}> | null = null;
let accessToken: string | null = null;
let loginRedirectStarted = false;

type RefreshResponse = {
    access_token?: string;
    token?: string;
    refresh_token?: string;
};

function setAccessTokenFromPayload(payload: RefreshResponse | undefined) {
    const token = payload?.access_token || payload?.token || '';
    accessToken = token || null;
}

export function getHttpStatus(error: unknown): number | undefined {
    return (error as AxiosError | undefined)?.response?.status;
}

export function isUnauthorizedError(error: unknown): boolean {
    return getHttpStatus(error) === 401 && !isMissingStaffProfile(error);
}

export function redirectToLogin(returnTo?: string) {
    if (typeof window === 'undefined' || loginRedirectStarted) {
        return;
    }

    loginRedirectStarted = true;
    const loginUrl = new URL('/login', IDP_ORIGIN || window.location.origin);
    loginUrl.searchParams.set('returnTo', returnTo || window.location.href);
    window.location.href = loginUrl.toString();
}

/**
 * Читаем fingerprint из cookie 'fp'
 * (сервер кладёт её в attachFpCookie, HttpOnly = false).
 */
function getFingerprintFromCookie(): string {
    return getFingerprint();
}

const api: AxiosInstance = axios.create({
    baseURL: GlobalVariable.host(),
    withCredentials: true,
    timeout: REQUEST_TIMEOUT_MS,
});

// ---------- REQUEST interceptor: добавляем X-CSRF-Token ----------
api.interceptors.request.use(
    (config) => {
        const fp = getFingerprintFromCookie();
        if (fp) {
            config.headers = config.headers || {};
            (config.headers as any)['X-CSRF-Token'] = fp;
        }
        const nativeOrCurrentToken = accessToken || getNativeAccessToken();
        if (nativeOrCurrentToken) {
            config.headers = config.headers || {};
            (config.headers as any)['Authorization'] = `Bearer ${nativeOrCurrentToken}`;
        } else if (config.headers) {
            delete (config.headers as any)['Authorization'];
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Обновление сессии.
 * Используем "голый" axios, чтобы на /api/refresh
 * не отрабатывал наш response-перехватчик и не было циклов.
 * Header X-CSRF-Token ставим вручную.
 */
async function refreshSession() {
    if (refreshPromise) {
        return refreshPromise;
    }

    const fp = getFingerprintFromCookie();
    const rt = getStoredRefreshToken();

    const headers: Record<string, string> = {};
    if (fp) {
        headers['X-CSRF-Token'] = fp;
    }
    if (rt) {
        headers['X-Refresh-Token'] = rt;
    }
    if (isInWebView()) {
        headers['X-Client'] = 'mobile';
    }
    Object.assign(headers, getNativeSessionModeHeaders());
    const refreshPayload = rt
        ? { refresh_token: rt }
        : getBrowserSessionProtocolPayload();

    refreshPromise = resolveNativeRefreshMode().then<{data: RefreshResponse}>(native => native ? requestNativeRefresh() : axios.post<RefreshResponse>(
        `${IDP_ORIGIN}/api/refresh`,
        refreshPayload, // browser capability либо explicit native refresh token
        {
            withCredentials: true,
            headers,
            timeout: REQUEST_TIMEOUT_MS,
        }
    )).then((response) => {
        setAccessTokenFromPayload(response.data);
        const token = response.data?.access_token || response.data?.token || '';
        const nextRefreshToken = response.data?.refresh_token || rt;
        if (response.data?.refresh_token) {
            persistRefreshToken(response.data.refresh_token);
        }
        exportSessionToNative({
            access_token: token,
            refresh_token: nextRefreshToken,
            fp,
            src: 'staff_refresh',
        });
        return response;
    }).catch((error) => {
        accessToken = null;
        if (getHttpStatus(error) === 401) {
            notifyNativeSessionInvalid(error?.response?.data?.error || '');
        }
        throw error;
    }).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

export type RefreshRolesResponse = {
    access_token?: string;
    roles?: string[];
    user_id?: number;
    error?: string;
};

export async function refreshRolesJwt(): Promise<RefreshRolesResponse> {
    await refreshSession().catch(() => null);

    const fp = getFingerprintFromCookie();
    const headers: Record<string, string> = {};
    if (fp) {
        headers['X-CSRF-Token'] = fp;
    }
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (isInWebView()) {
        headers['X-Client'] = 'mobile';
    }
    Object.assign(headers, getNativeSessionModeHeaders());

    const response = await axios.post<RefreshRolesResponse>(
        `${IDP_ORIGIN}/api/roles/refresh`,
        getBrowserSessionProtocolPayload(),
        {
            withCredentials: true,
            headers,
            timeout: REQUEST_TIMEOUT_MS,
        }
    );

    const token = response.data?.access_token || '';
    if (token) {
        setAccessTokenFromPayload({ access_token: token });
        exportSessionToNative({
            access_token: token,
            refresh_token: getStoredRefreshToken(),
            fp,
            src: 'staff_roles_refresh',
        });
    }

    return response.data || {};
}

// ---------- RESPONSE interceptor: обработка 401 и повтор запроса ----------
api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as RetryAxiosRequestConfig | undefined;

        // нет ответа / конфигурации или не 401 — просто пробрасываем ошибку
        if (!error.response || !originalRequest || error.response.status !== 401 || isMissingStaffProfile(error)) {
            return Promise.reject(error);
        }

        // чтобы не зациклиться — даём только одну попытку refresh для запроса
        if (originalRequest._retry) {
            return Promise.reject(error);
        }
        originalRequest._retry = true;

        try {
            // пробуем обновить токен
            await refreshSession();

            // повторяем исходный запрос уже через api (с интерцепторами)
            return api(originalRequest);
        } catch (refreshError) {
            const refreshErrorCode = (refreshError as any)?.response?.data?.error;
            // если refresh вернул fingerprint_mismatch / invalid_or_expired и т.п. — цепочка уже ревокнута
            // нужно отправить пользователя на логин
            if (
                (refreshError as any)?.response?.data &&
                typeof (refreshError as any).response.data === 'object' &&
                refreshErrorCode === 'fingerprint_mismatch'
            ) {
                // цепочка refresh уже revokeChain, логин обязателен
                console.warn('Fingerprint mismatch, redirecting to login');
            }

            if (getHttpStatus(refreshError) === 401 || refreshErrorCode === 'fingerprint_mismatch') {
                redirectToLogin();
            }

            return Promise.reject(refreshError);
        }
    }
);

export default api;
